import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection } from 'lexical';
import { BeautifulMentionsItemData } from 'lexical-beautiful-mentions';
import { $insertMentionAtSelection } from 'lexical-beautiful-mentions/mention-commands';
import { DYNAMIC_FIELD_TRIGGER } from '../constants';
import type { DynamicFieldAttributes } from '../molecules/dynamic-field-action';
import { $isDynamicFieldNode } from '../nodes/dynamic-field-node';
import { getStylesFromSelection } from '../utils/node-helpers';

/**
 * This hook is used to insert a dynamic field into the editor.
 * @returns A function that inserts a dynamic field into the editor.
 * @param selectedDynamicField - The dynamic field to insert.
 * @param noTransform - If true, the dynamic field will use the provided preview value instead of the symbol in the preview (e.g. "Matthew" instead of {{FIRST_NAME}}).
 * @param dynamicFieldStyles - The styles to apply to the dynamic field.
 */

export const useDynamicFieldInsertion = () => {
  const [editor] = useLexicalComposerContext();

  const insertDynamicField = (
    selectedDynamicField: DynamicFieldAttributes,
    noTransform?: boolean,
    dynamicFieldStyles?: React.CSSProperties
  ) => {
    editor.update(() => {
      const selection = $getSelection();

      if (selection !== null) {
        selection.getNodes().forEach((node) => {
          // if the selection node is a dynamic field node, it will be replaced by the new one
          if ($isDynamicFieldNode(node)) {
            node.remove();
          }
        });
      }

      const selectionStyles = getStylesFromSelection(selection);

      $insertMentionAtSelection([DYNAMIC_FIELD_TRIGGER], '', DYNAMIC_FIELD_TRIGGER, selectedDynamicField.symbol, {
        label: selectedDynamicField.label,
        val: selectedDynamicField.val ?? '',
        noTransform: !!noTransform,
        styles: (dynamicFieldStyles ?? selectionStyles) as unknown as BeautifulMentionsItemData,
      });
    });
  };

  return insertDynamicField;
};
